import { CONFIRMED_FINDINGS_CAVEAT } from '../../hooks/risk-from-scorecard.ts'
import {
  SCORECARD_DIMENSION_COUNT,
  SCORECARD_DIMENSIONS,
  type ScorecardDimensionId,
} from '../../utils/threat-model/scorecard-dimensions.ts'

/**
 * A short, plain gloss per scorecard dimension, for the "About risk" reference drawer.
 *
 * **Only the glosses are ours.** The ids and display names come from `SCORECARD_DIMENSIONS`, so the
 * reference drawer cannot name a dimension differently from the row and the per-repository drawer, which
 * read `displayName` straight off the scorecard response. Those were previously two hand-maintained
 * lists on different id vocabularies and had already drifted.
 *
 * The glosses exist because the names are long and jargon-heavy: a reader who has to parse
 * "Exposure-Adjusted Attack Surface" unaided has learned nothing. No severity vocabulary, for the same
 * reason `RISK_BANDS` carries none — severity and risk score are separate scales.
 *
 * Keyed rather than positional so a reorder in the rubric cannot silently pair a name with the wrong
 * gloss. The type makes a missing gloss a compile error when the rubric grows.
 */
const DIMENSION_GLOSSES: Record<ScorecardDimensionId, string> = {
  'exposure-adjusted-attack-surface': 'what untrusted callers can reach',
  'defense-in-depth-coverage': 'whether one failure is caught by another control',
  'blast-radius-containment': 'how far an intruder gets from a foothold',
  'authentication-posture': 'how identity is proven',
  'authorization-posture': 'how access decisions are enforced',
  'input-handling-hygiene': 'how untrusted data is validated before use',
  'secrets-and-credentials-handling': 'how keys and tokens are stored and rotated',
  'dependency-risk-posture': 'risk carried in by third-party packages',
  'transport-and-network-security': 'how traffic is protected in transit',
  'observability-and-detection': 'whether an attack leaves a visible trace',
}

/**
 * The dimensions a repository's risk score is derived from, in the order the scorecard reports them.
 *
 * Every dimension carries **equal weight** — stated in prose in the modal rather than encoded here:
 * there is no weight field, and the array order is presentation order, not rank. Adding either would
 * imply a hierarchy that does not exist.
 */
export const RISK_DIMENSIONS: readonly { id: ScorecardDimensionId; name: string; gloss: string }[] =
  SCORECARD_DIMENSIONS.map(({ dimension, displayName }) => ({
    id: dimension,
    name: displayName,
    gloss: DIMENSION_GLOSSES[dimension],
  }))

export type RiskDimension = (typeof RISK_DIMENSIONS)[number]

/**
 * The answer to "what is this number?", shared by the modal and its tests.
 *
 * Takes the product name rather than hardcoding it: this build white-labels, and an NTT install showing
 * "Pixee derives…" two elements below a header reading "Connected to Code Guardian™" contradicts itself.
 * `EnvironmentData.productName` is the same source the page's description uses.
 *
 * **Says what is missing as well as what is counted.** "Weighted equally" is true of the ten dimensions
 * *within* posture and not of exposure, which multiplies rather than averaging; and confirmed findings
 * are named as absent because the per-repository drawer says the same, and a reference that claimed them
 * as an input would contradict it.
 */
export const riskScoreSummary = (productName: string): string =>
  `${productName} derives a repository's risk from its threat-model scorecard: what its ` +
  `${SCORECARD_DIMENSION_COUNT} dimensions, each weighted equally, leave uncovered — scaled by how ` +
  `reachable the repository is from an untrusted network. ${CONFIRMED_FINDINGS_CAVEAT}.`

/**
 * Why this number and the Scorecard tab's disagree, said before a reader works it out and mistrusts both.
 *
 * They read the same scorecard in opposite directions: the tab reports **posture**, how much is in place,
 * where higher is better; this column reports **risk**, what is missing, where higher is worse. A reader
 * who checks one against the other will find they do not invert into each other, and the reason is
 * exposure — it scales risk and has no part in posture.
 *
 * Kept out of {@link riskScoreSummary} rather than appended to it: that paragraph explains what the
 * number is, and this answers a different question — why it is not the other number. Merged, the summary
 * would open on the score and close on a caveat about a different screen.
 */
export const riskVersusPostureNote = (): string =>
  'Risk counts what is missing, so higher is worse. The Scorecard tab reports the same dimensions as ' +
  'posture — what is in place — where higher is better. The two do not convert: only risk accounts for ' +
  'exposure.'
